import { Button } from '@yoast/ui-library';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { useGetBrands, useGetCurrentUser } from '../api/generated/insights';
import { useBrandAnalysisData, useBrandAnalysisMutations } from '../contexts';
import Layout from './Layout/Layout';
import ModalAlertBrandDisabled from './ModalAlertBrandDisabled';
import ModalUpgrade from './ModalUpgradeToAdd';
import RouteGuardLoading from './RouteGuards/RouteGuardLoading';

// Route constants for maintainability
const ROUTES = {
  HOME: '/',
  SETUP: '/setup',
  WELCOME: '/welcome',
  QUERIES: '/setup/:brandId/queries',
  ANALYSIS: '/setup/:brandId/analysis',
  BRAND_LIST: '/brand-list',
} as const;

// A protected route is one that should redirect the user away
//  when reloading the page if it is the wrong status
// const PROTECTED_ROUTES = [
//   ROUTES.SETUP,
//   ROUTES.QUERIES,
//   ROUTES.WELCOME,
//   // ROUTES.ANALYSIS, // no need to protect this route
// ] as string[];

// Status to route mapping
// const STATUS_ROUTE_MAP = {
//   pendingSetup: ROUTES.WELCOME,
//   pendingQuerySelection: ROUTES.QUERIES,
//   pendingBrandGrouping: ROUTES.BRAND_LIST,
//   active: ROUTES.HOME,
//   deleting: ROUTES.HOME,
//   deleted: ROUTES.HOME,
// } as const;

const Initializer: React.FC<{
  hideNav?: boolean;
  disableLayout?: boolean;
  DebugInfo?: React.FC;
}> = ({ hideNav = false, disableLayout = false, DebugInfo }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showUpgradeModal, setShowUpgradeModal] = React.useState(
    // NOTE: even though it defaults to show,
    // it is only rendered based on the logic below
    true,
  );

  const layoutProps = {
    disable: disableLayout,
    hideNav,
  };

  const { brandId } = useBrandAnalysisData();
  const { setBrandId } = useBrandAnalysisMutations();

  const {
    data: brands,
    isLoading: isLoadingBrands,
    error: isErrorBrands,
    refetch,
  } = useGetBrands({
    query: {
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      select: (data) => data?.data?.brands,
    },
  });

  const { data: currentUserData } = useGetCurrentUser();

  // Track if initial routing has been completed
  const hasInitializedRouting = useRef(false);

  // Memoize the selected brand to avoid recalculation
  const selectedBrand = useMemo(() => {
    return brands?.find((brand) => brand.id === brandId);
  }, [brands, brandId]);

  // TODO: move to BrandAnalysis parent
  // useEffect(() => {
  //   // if brands list is empty, redirect to welcome
  //   if (brands?.length === 0 && !location.pathname.startsWith('/setup')) {
  //     navigate(ROUTES.WELCOME);
  //   }
  //   // only do this one time on load
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  // Effect 1: Handle initial brandId setting
  useEffect(() => {
    if (brands === undefined || brandId) return;

    const firstBrandId = brands[0]?.id;
    if (firstBrandId) {
      setBrandId(firstBrandId);
    }
  }, [brands, brandId, setBrandId, navigate, location.pathname]);

  // Effect 2: Handle navigation based on brand status
  useEffect(() => {
    if (!brandId || hasInitializedRouting.current) {
      return;
    }

    // TODO
    // const targetRoute =
    //   STATUS_ROUTE_MAP[selectedBrand.status] || STATUS_ROUTE_MAP.pendingSetup;

    // if (
    //   targetRoute &&
    //   location.pathname !== targetRoute &&
    //   PROTECTED_ROUTES.includes(location.pathname)
    // ) {
    //   navigate(targetRoute);
    // }

    // Mark routing as initialized to prevent future redirects
    hasInitializedRouting.current = true;
  }, [selectedBrand, brandId, navigate, location.pathname, brands]);

  // Retry handler using refetch instead of page reload
  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  // // Show loading state
  if (location.pathname !== ROUTES.HOME && isLoadingBrands) {
    return (
      <Layout {...layoutProps}>
        <RouteGuardLoading />
      </Layout>
    );
  }

  // Show error state
  // TODO: review with #ux
  if (isErrorBrands) {
    return (
      <Layout {...layoutProps}>
        <div className="yst-flex yst-min-h-screen yst-items-center yst-justify-center">
          <div className="yst-space-y-4 yst-text-center">
            <div className="yst-mb-4 yst-text-6xl yst-text-red-600">⚠️</div>
            <h2 className="yst-text-xl yst-font-semibold yst-text-slate-900">
              Something went wrong
            </h2>
            <p className="yst-text-slate-600">
              We couldn't load your Brand Analysis. Please try again shortly.
            </p>
            <Button variant="primary" onClick={handleRetry}>
              Retry
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  // Subscription / upsell gating rules:
  // - Show the upgrade modal when the user has zero active subscriptions and is not in the trial grace period.
  // - Suppress the modal for 8 hours after trial analysis completes to let the user explore.
  // - Users with existing brands or who are eligible for a free trial can dismiss the modal and continue without upgrading.
  // - Users without brands and not trial-eligible cannot dismiss; the modal hides close controls to require an upgrade.
  const trialCompletedAt = currentUserData?.data?.trialAnalysisCompletedAt;
  const isWithinTrialGracePeriod =
    !!trialCompletedAt &&
    Date.now() - new Date(trialCompletedAt).getTime() < 8 * 60 * 60 * 1000;
  const shouldShowUpgradeModal =
    currentUserData?.data?.activeSubscriptions === 0 &&
    !isWithinTrialGracePeriod;

  // Allow user to bypass if they have active brands even if no active subs
  const canDismissUpsellModal =
    currentUserData?.data?.brands && currentUserData?.data?.brands?.length > 0;

  // All checks passed - render the nested routes
  return (
    <Layout DebugInfo={DebugInfo} {...layoutProps}>
      <Outlet />
      <ModalAlertBrandDisabled />
      {shouldShowUpgradeModal && (
        <ModalUpgrade
          isOpen={showUpgradeModal}
          variant={
            currentUserData?.data?.isEligibleForFreeTrial
              ? 'trial'
              : 'purchase'
          }
          onClose={
            canDismissUpsellModal ||
            currentUserData?.data?.isEligibleForFreeTrial
              ? () => {
                  setShowUpgradeModal(false);
                }
              : // passing undefined hides the close buttons in the modal
                undefined
          }
        />
      )}
    </Layout>
  );
};

export default Initializer;
